import { createReducer, on } from '@ngrx/store';
import { Option } from '@sapphire/result';

import { SupplierState } from '../survey-state.model';
import { INITIAL_SUPPLIER_STATE } from '../survey-state.constants';
import { SupplierActions, SupplierApiActions } from './supplier.actions';

export const supplierReducer = createReducer(
  INITIAL_SUPPLIER_STATE,

  on(
    SupplierActions.loadAllocations,
    (state): SupplierState => ({
      ...state,
      allocationsLoading: true,
      allocationsError: null,
    }),
  ),

  on(
    SupplierActions.loadBlends,
    (state): SupplierState => ({
      ...state,
      blendsLoading: true,
      blendsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadBlendsSuccess,
    (state, { blends }): SupplierState => ({
      ...state,
      blends,
      blendsLoading: false,
      blendsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadBlendsFailure,
    (state, { error }): SupplierState => ({
      ...state,
      blends: [],
      blendsLoading: false,
      blendsError: error,
    }),
  ),

  on(
    SupplierActions.loadGroupTemplates,
    (state): SupplierState => ({
      ...state,
      groupTemplatesLoading: true,
      groupTemplatesLoadError: null,
    }),
  ),

  on(
    SupplierApiActions.loadGroupTemplatesSuccess,
    (state, { groupTemplates }): SupplierState => ({
      ...state,
      groupTemplates,
      groupTemplatesLoading: false,
      groupTemplatesLoadError: null,
    }),
  ),

  on(
    SupplierApiActions.loadGroupTemplatesFailure,
    (state, { error }): SupplierState => ({
      ...state,
      groupTemplates: [],
      groupTemplatesLoading: false,
      groupTemplatesLoadError: error,
    }),
  ),

  on(
    SupplierActions.loadFlexibilityPriceParams,
    (state): SupplierState => ({
      ...state,
      flexibilityPriceParamsLoading: true,
      flexibilityPriceParamsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadFlexibilityPriceParamsSuccess,
    (state, { params }): SupplierState => ({
      ...state,
      flexibilityPriceParams: params,
      flexibilityPriceParamsLoading: false,
      flexibilityPriceParamsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadFlexibilityPriceParamsFailure,
    (state, { error }): SupplierState => ({
      ...state,
      flexibilityPriceParams: null,
      flexibilityPriceParamsLoading: false,
      flexibilityPriceParamsError: error,
    }),
  ),

  on(
    SupplierApiActions.loadAllocationsSuccess,
    (state, { allocations }): SupplierState => {
      return {
        ...state,
        allocations,
        allocationsLoading: false,
        allocationsError: null,
      };
    },
  ),

  on(
    SupplierApiActions.loadAllocationsFailure,
    (state, { error }): SupplierState => ({
      ...state,
      allocations: [],
      allocationsLoading: false,
      allocationsError: error,
    }),
  ),

  // Patches only `isActive` on the existing single-supplier allocations, matched
  // by id, so a recalculation's active/inactive flip is reflected without
  // discarding the user's other local edits. Grouped allocations and rows the
  // fetch does not mention are left as they are.
  on(
    SupplierApiActions.syncAllocationsActiveState,
    (state, { allocations }): SupplierState => {
      const activeById = new Map<number, boolean>();
      for (const fetched of allocations) {
        if (!fetched.isGroup) {
          activeById.set(fetched.id, fetched.isActive);
        }
      }

      return {
        ...state,
        allocations: state.allocations.map((existing) => {
          if (existing.isGroup) return existing;
          return Option.from(activeById.get(existing.id))
            .filter((nextActive) => nextActive !== existing.isActive)
            .map((nextActive) => ({ ...existing, isActive: nextActive }))
            .unwrapOr(existing);
        }),
      };
    },
  ),

  on(
    SupplierActions.updateAllocations,
    SupplierActions.updateManagedAllocations,
    (state): SupplierState => ({
      ...state,
      allocationsUpdateStatus: 'saving',
    }),
  ),

  // The PUT returns the survey's updated allocations, so its response replaces
  // the list outright — no follow-up GET is needed.
  on(
    SupplierApiActions.updateAllocationsSuccess,
    (state, { allocations }): SupplierState => ({
      ...state,
      allocations,
      allocationsLoading: false,
      allocationsError: null,
      allocationsUpdateStatus: 'saved',
    }),
  ),

  // The previous list survives a failed update so the drawer can stay open on
  // the user's unsaved edits. The message itself is not kept — the effect's
  // toast already reported it and nothing renders it from state.
  on(
    SupplierApiActions.updateAllocationsFailure,
    (state): SupplierState => ({
      ...state,
      allocationsUpdateStatus: 'failed',
    }),
  ),

  on(
    SupplierActions.saveGroupAsTemplate,
    (state): SupplierState => ({
      ...state,
      groupTemplateSaving: true,
      groupTemplateSaveError: null,
    }),
  ),

  on(
    SupplierApiActions.saveGroupAsTemplateSuccess,
    (state): SupplierState => ({
      ...state,
      groupTemplateSaving: false,
      groupTemplateSaveError: null,
    }),
  ),

  on(
    SupplierApiActions.saveGroupAsTemplateFailure,
    (state, { error }): SupplierState => ({
      ...state,
      groupTemplateSaving: false,
      groupTemplateSaveError: error,
    }),
  ),

  on(
    SupplierActions.saveBlend,
    (state): SupplierState => ({
      ...state,
      blendSaving: true,
      blendSaveError: null,
      savedBlendId: null,
    }),
  ),

  on(
    SupplierApiActions.saveBlendSuccess,
    (state, { blend }): SupplierState => ({
      ...state,
      blendSaving: false,
      blendSaveError: null,
      savedBlendId: blend.id,
    }),
  ),

  on(
    SupplierApiActions.saveBlendFailure,
    (state, { error }): SupplierState => ({
      ...state,
      blendSaving: false,
      blendSaveError: error,
      savedBlendId: null,
    }),
  ),

  on(
    SupplierActions.resetAllocations,
    (): SupplierState => ({
      allocations: [],
      allocationsLoading: false,
      allocationsError: null,
      allocationsUpdateStatus: 'idle',
      blends: [],
      blendsLoading: false,
      blendsError: null,
      groupTemplates: [],
      groupTemplatesLoading: false,
      groupTemplatesLoadError: null,
      flexibilityPriceParams: null,
      flexibilityPriceParamsLoading: false,
      flexibilityPriceParamsError: null,
      groupTemplateSaving: false,
      groupTemplateSaveError: null,
      blendSaving: false,
      blendSaveError: null,
      savedBlendId: null,
      rateCards: null,
      rateCardsLoading: false,
      rateCardsError: null,
    }),
  ),

  on(
    SupplierActions.loadRateCards,
    (state): SupplierState => ({
      ...state,
      rateCardsLoading: true,
      rateCardsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadRateCardsSuccess,
    (state, { rateCards }): SupplierState => ({
      ...state,
      rateCards,
      rateCardsLoading: false,
      rateCardsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadRateCardsFailure,
    (state, { error }): SupplierState => ({
      ...state,
      rateCards: null,
      rateCardsLoading: false,
      rateCardsError: error,
    }),
  ),
);
